import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollText, Download, Search, RotateCcw } from "lucide-react";

const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => { try { return new Date(iso).toLocaleString("pt-BR"); } catch { return iso; } };

export default function Auditoria() {
  const { scopeParams, selectedSchoolId } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ actions: [], entities: [] });
  const [filters, setFilters] = useState({ action: "all", entity: "all", q: "", date_from: "", date_to: "" });
  const [loading, setLoading] = useState(false);

  const params = useCallback(() => {
    const p = { ...scopeParams };
    if (filters.action !== "all") p.action = filters.action;
    if (filters.entity !== "all") p.entity = filters.entity;
    if (filters.q) p.q = filters.q;
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    return p;
  }, [filters, scopeParams]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/audit", { params: params() }).then((r) => setRows(r.data)).catch(() => setRows([])).finally(() => setLoading(false));
  }, [params]);

  useEffect(() => {
    api.get("/audit/actions", { params: scopeParams }).then((r) => setMeta(r.data)).catch(() => {});
  }, [scopeParams, selectedSchoolId]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const qs = new URLSearchParams(params()).toString();
    window.open(`${apiBase}/audit/export?${qs}`, "_blank", "noopener");
  };
  const reset = () => setFilters({ action: "all", entity: "all", q: "", date_from: "", date_to: "" });

  return (
    <div>
      <PageHeader title="Auditoria" subtitle="Registro de operações relevantes na plataforma">
        <Button variant="outline" onClick={exportCsv} data-testid="audit-export-button"><Download className="mr-1.5 h-4 w-4" /> Exportar CSV</Button>
      </PageHeader>

      <Card className="mb-5 border-slate-200 p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Select value={filters.action} onValueChange={(v) => setFilters((f) => ({ ...f, action: v }))}>
            <SelectTrigger data-testid="audit-action-filter"><SelectValue placeholder="Ação" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as ações</SelectItem>
              {meta.actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.entity} onValueChange={(v) => setFilters((f) => ({ ...f, entity: v }))}>
            <SelectTrigger data-testid="audit-entity-filter"><SelectValue placeholder="Entidade" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as entidades</SelectItem>
              {meta.entities.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} data-testid="audit-date-from" />
          <Input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} data-testid="audit-date-to" />
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Usuário ou ID..." className="pl-9" data-testid="audit-search" />
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <Button variant="ghost" size="sm" onClick={reset} data-testid="audit-reset"><RotateCcw className="mr-1.5 h-4 w-4" /> Limpar filtros</Button>
        </div>
      </Card>

      {rows.length === 0 ? (
        <EmptyState icon={ScrollText} title={loading ? "Carregando..." : "Sem registros"} description="Nenhum evento encontrado para os filtros atuais." />
      ) : (
        <Card className="overflow-x-auto border-slate-200" data-testid="audit-table">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Data/Hora</TableHead><TableHead>Usuário</TableHead>
              <TableHead>Ação</TableHead><TableHead>Entidade</TableHead><TableHead>ID</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`audit-row-${r.id}`}>
                  <TableCell className="whitespace-nowrap text-xs text-slate-500">{fmt(r.created_at)}</TableCell>
                  <TableCell>
                    <div className="text-sm font-medium text-slate-800">{r.user_name || "—"}</div>
                    <div className="text-xs text-slate-400">{r.user_role}</div>
                  </TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{r.action}</Badge></TableCell>
                  <TableCell className="text-sm text-slate-600">{r.entity}</TableCell>
                  <TableCell className="max-w-[200px] truncate text-xs text-slate-400">{r.entity_id}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
